import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ShoppingBag, Truck, Warning } from "@phosphor-icons/react";
import Nav from "@/components/site/Nav";
import { useCart, hydrateCart } from "@/lib/cart";
import { fmt, Field, US_STATES } from "@/pages/checkout/checkoutHelpers";
import OrderSummary from "@/pages/checkout/OrderSummary";
import ColorSizePicker from "@/pages/shop/ColorSizePicker";
import SizeGuideModal from "@/pages/shop/SizeGuideModal";
import { identify as swsIdentify, trackConversion, getAttributionSnapshot } from "@/lib/analyticsClient";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function MerchCheckout() {
  const [params] = useSearchParams();
  const productId = params.get("product");
  const variantIdParam = params.get("variant");
  const isCartMode = params.get("cart") === "1";
  const cart = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [variantId, setVariantId] = useState(variantIdParam ? Number(variantIdParam) : null);
  const [quantity, setQuantity] = useState(1);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);

  const [recipient, setRecipient] = useState({
    name: "", email: "", phone: "",
    address1: "", address2: "",
    city: "", state_code: "", country_code: "US", zip: "",
  });
  const [rates, setRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Currently active payment provider — drives the Pay button label so it
  // stays truthful when the admin flips Stripe ↔ Square. Default to "stripe"
  // for the older UX until the fetch settles; falls back gracefully if the
  // endpoint is unreachable.
  const [provider, setProvider] = useState("stripe");

  useEffect(() => {
    let alive = true;
    const refetch = () => {
      fetch(`${BACKEND}/api/payments/provider`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (alive && d?.provider) setProvider(d.provider); })
        .catch(() => {});
    };
    refetch();
    // Re-check when the customer returns to the tab (admin may have flipped
    // Stripe ↔ Square in another tab). Guarantees the Pay button + backend
    // routing stay in lockstep with the current admin toggle.
    window.addEventListener("focus", refetch);
    return () => { alive = false; window.removeEventListener("focus", refetch); };
  }, []);

  // Pre-fill recipient email if the user came from the Sacrifice signup form
  useEffect(() => {
    try {
      const pendingEmail = window.localStorage.getItem("sws_pending_signup_email") || "";
      if (pendingEmail) setRecipient((r) => (r.email ? r : { ...r, email: pendingEmail }));
    } catch (err) { console.warn("MerchCheckout: pending-email prefill failed:", err); }
  }, []);

  // Heal legacy cart items missing price/image (revenue-blocking $0 subtotal bug).
  useEffect(() => {
    if (!isCartMode) return;
    hydrateCart(BACKEND).catch((err) => console.warn("MerchCheckout: cart hydration failed:", err));
  }, [isCartMode]);

  useEffect(() => {
    if (!productId) return;
    fetch(`${BACKEND}/api/shop/products/${productId}`)
      .then((r) => r.json())
      .then((d) => {
        setProduct(d);
        // Auto-select the variant if passed; otherwise first
        const v = d.variants?.find((x) => Number(x.id) === Number(variantIdParam)) || d.variants?.[0];
        if (v) setVariantId(v.id);
      })
      .catch(() => setError("Could not load product."))
      .finally(() => setLoading(false));
  }, [productId, variantIdParam]);

  const variant = useMemo(
    () => product?.variants?.find((v) => Number(v.id) === Number(variantId)) || null,
    [product, variantId]
  );

  // Ordered list of mockup images for the current variant + fallbacks
  const mockups = useMemo(() => {
    const list = [];
    if (variant?.mockups?.length) list.push(...variant.mockups);
    else if (variant?.preview_url) list.push(variant.preview_url);
    if (product?.thumbnail_url && !list.includes(product.thumbnail_url)) list.push(product.thumbnail_url);
    if (product?.images) product.images.forEach((img) => { if (!list.includes(img)) list.push(img); });
    return list;
  }, [variant, product]);

  const [mockupIdx, setMockupIdx] = useState(0);
  // Reset to first image whenever variant changes
  useEffect(() => { setMockupIdx(0); }, [variantId]);
  const clampedIdx = mockups.length ? Math.min(mockupIdx, mockups.length - 1) : 0;
  const activeMockup = mockups[clampedIdx];
  const nextMockup = () => setMockupIdx((i) => (i + 1) % mockups.length);
  const prevMockup = () => setMockupIdx((i) => (i - 1 + mockups.length) % mockups.length);

  const itemPrice = variant?.retail_price ? Number(variant.retail_price) : 0;
  // In cart mode we sum across all cart items; otherwise the single product×qty
  const cartSubtotal = cart.items.reduce((s, it) => s + Number(it.item_retail_price || 0) * Number(it.quantity || 0), 0);
  const itemTotal = isCartMode ? cartSubtotal : itemPrice * quantity;
  const shipping = selectedRate ? Number(selectedRate.rate) : 0;
  const grandTotal = itemTotal + shipping;

  const shippingComplete = recipient.name && recipient.address1 && recipient.city && recipient.zip && recipient.country_code && (recipient.country_code !== "US" || recipient.state_code);

  // Build the shipping-quote items[] payload based on mode
  const quoteItems = isCartMode
    ? cart.items.map((it) => ({
        sync_variant_id: Number(it.sync_variant_id),
        variant_id: it.variant_id ? Number(it.variant_id) : undefined,
        quantity: Number(it.quantity),
      }))
    : (variantId ? [{
        sync_variant_id: Number(variantId),
        variant_id: variant?.variant_id ? Number(variant.variant_id) : undefined,
        quantity,
      }] : []);

  const requestQuote = async () => {
    setQuoteError("");
    setRates([]);
    setSelectedRate(null);
    if (!shippingComplete || quoteItems.length === 0) return;
    setQuoteLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/merch/shipping-quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recipient, items: quoteItems }),
      });
      // Guard against non-JSON bodies (e.g. an intermittent HTML gateway
      // page) so the customer never sees a raw JSON parse error.
      const raw = await r.text();
      let data = null;
      try { data = raw ? JSON.parse(raw) : null; } catch (_e) { data = null; }
      if (!r.ok) throw new Error(data?.detail || "We couldn't calculate shipping right now. Please double-check the address.");
      if (!data?.rates || data.rates.length === 0) throw new Error("No shipping methods available to this address.");
      setRates(data.rates);
      // Auto-select the cheapest option (Printful returns them ordered but be
      // defensive and sort by rate anyway).
      const sorted = [...data.rates].sort((a, b) => Number(a.rate) - Number(b.rate));
      setSelectedRate(sorted[0]);
    } catch (e) {
      setQuoteError(e.message || "Shipping isn't available for this address just yet — try again in a moment.");
    } finally {
      setQuoteLoading(false);
    }
  };

  // Auto-quote shipping the moment the address + items are complete, and
  // re-quote whenever any of those change. Debounced to avoid firing on
  // every keystroke.
  useEffect(() => {
    if (!shippingComplete || quoteItems.length === 0) return;
    const t = setTimeout(() => { requestQuote(); }, 600);
    return () => clearTimeout(t);
  }, [
    shippingComplete,
    recipient.address1, recipient.address2, recipient.city,
    recipient.state_code, recipient.country_code, recipient.zip,
    // Re-quote when cart contents change (add/remove item or quantity)
    JSON.stringify(quoteItems),
  ]);

  const checkout = async () => {
    setError("");
    if (!selectedRate || !shippingComplete) {
      setError("Complete address + shipping rate before checkout.");
      return;
    }
    if (!recipient.email) {
      setError("Email is required so Printful can send tracking.");
      return;
    }
    if (isCartMode && cart.items.length === 0) {
      setError("Cart is empty.");
      return;
    }
    if (!isCartMode && !variant) {
      setError("Pick a variant first.");
      return;
    }
    setSubmitting(true);
    // Perf trace — logged to browser console so we can prove exactly where
    // the click-to-Square latency is spent. Look for "[checkout timing]"
    // in DevTools → Console after clicking Complete Purchase.
    const _perfStart = performance.now();
    const _perfMark = (label) => {
      console.log(`[checkout timing] ${label}: ${(performance.now() - _perfStart).toFixed(0)}ms`);
    };
    try {
      // If user came from the Sacrifice signup form, thread the signup_id
      // so the backend can auto-confirm the Legacy Wall entry after checkout.
      let pendingSignupId = "";
      try { pendingSignupId = window.localStorage.getItem("sws_pending_signup") || ""; } catch (err) { console.warn("MerchCheckout: pending signup_id read failed:", err); }
      const body = isCartMode
        ? {
            items: cart.items.map((it) => ({
              product_id: Number(it.product_id),
              sync_variant_id: Number(it.sync_variant_id),
              variant_id: it.variant_id ? Number(it.variant_id) : undefined,
              quantity: Number(it.quantity),
              item_retail_price: Number(it.item_retail_price),
              product_name: it.product_name,
              variant_name: it.variant_name || "",
            })),
            recipient,
            shipping_rate_id: selectedRate.id,
            shipping_amount: shipping,
            origin_url: window.location.origin,
            signup_id: pendingSignupId || undefined,
          }
        : {
            product_id: Number(productId),
            sync_variant_id: Number(variantId),
            quantity,
            recipient,
            shipping_rate_id: selectedRate.id,
            shipping_amount: shipping,
            item_retail_price: itemPrice,
            product_name: product?.name || "",
            variant_name: variant?.name || "",
            origin_url: window.location.origin,
            signup_id: pendingSignupId || undefined,
          };
      _perfMark("about to POST /api/merch/checkout");
      const r = await fetch(`${BACKEND}/api/merch/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Attach the analytics attribution snapshot so this order can be
        // credited to its traffic source (IG/LinkedIn/email/QR/etc.) even
        // after payment lands via webhook.
        body: JSON.stringify({ ...body, attribution: getAttributionSnapshot() }),
      });
      _perfMark("response headers received");
      // Response body may be HTML (e.g. Cloudflare 5xx page) instead of JSON.
      // Grab as text first, then attempt JSON parse. This prevents the raw
      // "Unexpected token '<'" error from bubbling to the user.
      const rawBody = await r.text();
      let data = null;
      try { data = rawBody ? JSON.parse(rawBody) : null; } catch (_e) { data = null; }
      _perfMark("body parsed");
      if (!r.ok || !data?.url) {
        const msg = data?.detail
          || (r.status >= 500 ? "Payment service is temporarily unavailable — please try again in a moment."
                              : "Checkout failed. Please review your details and try again.");
        throw new Error(typeof msg === "string" ? msg : "Checkout failed.");
      }
      // Clear cart on successful checkout redirect (works for both Stripe & Square)
      if (isCartMode) cart.clear();
      // Sweat With Slay analytics — identify session by email + record "started_checkout".
      // Fire-and-forget — never await these so they can't delay the redirect.
      try {
        if (recipient?.email) swsIdentify(recipient.email, "merch_checkout_started", recipient.name || "");
        trackConversion("merch_checkout_started", {
          email: recipient?.email || "",
          value: Number((data?.amount_total || 0)),
          refId: data?.session_id || data?.order_id || "",
        });
      } catch { /* silent */ }
      _perfMark(`redirecting → ${data.url.slice(0, 60)}...`);
      window.location.href = data.url;
    } catch (e) {
      setError(e.message || "Checkout failed.");
      setSubmitting(false);
    }
  };

  // Cart mode guard — if user opens /merch/checkout?cart=1 but cart is empty, redirect
  if (isCartMode && cart.items.length === 0 && !submitting) {
    return (
      <main className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6 pt-24 font-body">
        <Nav />
        <div className="max-w-md text-center space-y-4">
          <p className="text-white/70">Your cart is empty.</p>
          <Link to="/shop" className="text-[#B32027] uppercase tracking-[0.3em] text-xs font-bold">→ Browse Shop</Link>
        </div>
      </main>
    );
  }

  if (!productId && !isCartMode) {
    return (
      <main className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <p className="text-white/70 mb-6">No product selected.</p>
          <Link to="/shop" className="text-[#B32027] uppercase tracking-[0.3em] text-xs font-bold">→ Browse Shop</Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="merch-checkout-page" className="bg-[#0A0A0A] text-white font-body min-h-screen pt-16">
      <Nav />
      {/* Full-screen "Redirecting to Square/Stripe" overlay — appears the
          instant the customer taps "Complete purchase" and stays visible
          until the hosted checkout page takes over. Without this, the ~500 ms
          of API + 1-3 s of Square page render feels like the app froze. */}
      {submitting && (
        <div
          data-testid="checkout-redirecting-overlay"
          className="fixed inset-0 z-[9999] bg-[#0A0A0A]/95 backdrop-blur-sm flex items-center justify-center px-6"
        >
          <div className="max-w-lg text-center space-y-6">
            <div className="inline-flex h-14 w-14 items-center justify-center">
              <div className="h-12 w-12 border-4 border-white/15 border-t-[#B32027] rounded-full animate-spin" />
            </div>
            <div className="space-y-3">
              <p className="text-[#B32027] uppercase tracking-[0.35em] text-[10px] font-bold">
                / One moment
              </p>
              <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-3xl md:text-4xl">
                Sending you to<br />
                secure {provider === "square" ? "Square" : "Stripe"} checkout…
              </h2>
              <p className="text-white/65 text-sm leading-relaxed">
                Your card details never touch our servers — they go straight
                to {provider === "square" ? "Square" : "Stripe"}. This usually
                takes 2-3 seconds.
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="border-b border-white/10 px-6 md:px-12 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/shop" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-white/70 hover:text-[#B32027]">
            <ArrowLeft size={12} weight="bold" /> Shop
          </Link>
          <p className="text-[10px] uppercase tracking-[0.4em] text-white/40">/ Secure checkout · {provider === "square" ? "Square" : "Stripe"}</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 grid md:grid-cols-[1fr_400px] gap-10">
        {/* Left: form */}
        <div className="space-y-8">
          <div>
            <p className="text-[#B32027] uppercase tracking-[0.4em] text-[10px] font-bold mb-3">/ Legacy merch checkout</p>
            <h1 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tighter leading-[0.9]">
              Ship the<br /><span className="text-[#B32027]">tribute.</span>
            </h1>
          </div>

          {loading && !isCartMode ? (
            <p className="text-white/60 uppercase tracking-[0.3em] text-xs">Loading product…</p>
          ) : (
            <>
              {/* Mockup gallery — hero image + arrows + thumbnails */}
              {mockups.length > 0 && (
                <section data-testid="mockup-gallery" className="border border-white/15 bg-[#141414]">
                  <div className="relative aspect-square overflow-hidden bg-white">
                    <img
                      key={activeMockup}
                      src={activeMockup}
                      alt={variant?.name || product?.name || "Product"}
                      data-testid="mockup-hero-image"
                      className="w-full h-full object-contain transition-opacity duration-300"
                    />
                    {mockups.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={prevMockup}
                          data-testid="mockup-prev"
                          aria-label="Previous mockup"
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-black/70 hover:bg-[#B32027] text-white border-2 border-white/20 hover:border-[#B32027] transition-colors backdrop-blur"
                        >
                          <ArrowLeft size={18} weight="bold" />
                        </button>
                        <button
                          type="button"
                          onClick={nextMockup}
                          data-testid="mockup-next"
                          aria-label="Next mockup"
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-black/70 hover:bg-[#B32027] text-white border-2 border-white/20 hover:border-[#B32027] transition-colors backdrop-blur"
                        >
                          <ArrowRight size={18} weight="bold" />
                        </button>
                        <span data-testid="mockup-counter" className="absolute bottom-3 right-3 px-2 py-1 text-[10px] uppercase tracking-[0.2em] font-bold text-white bg-black/70 border border-white/20">
                          {clampedIdx + 1} / {mockups.length}
                        </span>
                      </>
                    )}
                  </div>
                  {mockups.length > 1 && (
                    <div className="flex gap-2 p-3 overflow-x-auto bg-[#0F0F0F]">
                      {mockups.map((m, i) => (
                        <button
                          key={m + i}
                          type="button"
                          onClick={() => setMockupIdx(i)}
                          data-testid={`mockup-thumb-${i}`}
                          className={`w-16 h-16 flex-shrink-0 border-2 bg-white transition-colors ${
                            clampedIdx === i ? "border-[#B32027]" : "border-white/10 hover:border-white/40"
                          }`}
                          aria-label={`View mockup ${i + 1}`}
                        >
                          <img src={m} alt="" className="w-full h-full object-contain" />
                        </button>
                      ))}
                    </div>
                  )}
                </section>
              )}

              {/* Color + Size picker — customer arrives with a pre-picked variant
                  via ?variant=X, but can change either dimension right here on the
                  checkout page. */}
              {product?.variants?.length > 1 && (
                <section className="border border-white/15 bg-[#0F0F0F] p-5">
                  <ColorSizePicker
                    variants={product.variants}
                    value={variant}
                    onChange={(v) => v && setVariantId(v.id)}
                    onValidityChange={() => { /* checkout button already checks variantId */ }}
                    onOpenSizeGuide={() => setSizeGuideOpen(true)}
                  />
                </section>
              )}

              <section className="border border-white/15 bg-[#0F0F0F] p-5 space-y-3">
                <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/60">Quantity</p>
                <div className="flex items-center gap-3">
                  <button onClick={() => setQuantity((q) => Math.max(1, q - 1))} className="w-9 h-9 border border-white/20 hover:border-[#B32027] text-white">−</button>
                  <span className="w-10 text-center text-lg font-bold" data-testid="qty-value">{quantity}</span>
                  <button onClick={() => setQuantity((q) => Math.min(10, q + 1))} className="w-9 h-9 border border-white/20 hover:border-[#B32027] text-white">+</button>
                </div>
              </section>

              {/* Address */}
              <section className="border border-white/15 bg-[#0F0F0F] p-5 space-y-4">
                <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/60 flex items-center gap-2">
                  <Truck size={12} weight="fill" className="text-[#B32027]" /> Ship to
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Full name" value={recipient.name} onChange={(v) => setRecipient({ ...recipient, name: v })} testid="ship-name" required />
                  <Field label="Email" type="email" value={recipient.email} onChange={(v) => setRecipient({ ...recipient, email: v })} testid="ship-email" required />
                  <Field label="Phone (optional)" value={recipient.phone} onChange={(v) => setRecipient({ ...recipient, phone: v })} testid="ship-phone" />
                  <Field label="Country" value={recipient.country_code} onChange={(v) => setRecipient({ ...recipient, country_code: v.toUpperCase().slice(0,2) })} testid="ship-country" required />
                  <Field className="sm:col-span-2" label="Street address" value={recipient.address1} onChange={(v) => setRecipient({ ...recipient, address1: v })} testid="ship-address1" required />
                  <Field className="sm:col-span-2" label="Apt / suite (optional)" value={recipient.address2} onChange={(v) => setRecipient({ ...recipient, address2: v })} testid="ship-address2" />
                  <Field label="City" value={recipient.city} onChange={(v) => setRecipient({ ...recipient, city: v })} testid="ship-city" required />
                  {recipient.country_code === "US" ? (
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.25em] font-semibold text-white/50">State</label>
                      <select data-testid="ship-state" value={recipient.state_code} onChange={(e) => setRecipient({ ...recipient, state_code: e.target.value })} className="w-full mt-1 bg-black border border-white/15 px-3 py-2.5 text-white focus:border-[#B32027] outline-none">
                        <option value="">—</option>
                        {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  ) : (
                    <Field label="State / region" value={recipient.state_code} onChange={(v) => setRecipient({ ...recipient, state_code: v })} testid="ship-state" />
                  )}
                  <Field label="ZIP / postal code" value={recipient.zip} onChange={(v) => setRecipient({ ...recipient, zip: v })} testid="ship-zip" required />
                </div>
              </section>

              {/* Shipping quote — auto-calculated. Fires whenever the address
                  fields become complete, or the cart / quantity changes. */}
              <section className="border border-white/15 bg-[#0F0F0F] p-5 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/60">Shipping method</p>
                  {quoteLoading && (
                    <span data-testid="shipping-quote-loading" className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/60">
                      Calculating shipping…
                    </span>
                  )}
                  {!quoteLoading && rates.length > 0 && (
                    <button
                      onClick={requestQuote}
                      data-testid="shipping-quote-recalc"
                      className="text-[10px] uppercase tracking-[0.25em] font-bold border border-white/20 hover:border-white/50 text-white/70 hover:text-white px-3 py-2"
                    >
                      Recalculate
                    </button>
                  )}
                </div>
                {quoteError && (
                  <p data-testid="shipping-quote-error" className="text-red-400 text-xs leading-relaxed flex items-start gap-2">
                    <Warning size={12} weight="fill" className="mt-0.5 flex-shrink-0" />
                    <span>{quoteError}</span>
                  </p>
                )}
                {rates.length > 0 && (
                  <div className="space-y-2" data-testid="shipping-rates">
                    {rates.map((rt) => (
                      <label key={rt.id} className={`flex items-center justify-between gap-3 border-2 px-4 py-3 cursor-pointer transition-colors ${selectedRate?.id === rt.id ? "border-[#B32027] bg-[#B32027]/5" : "border-white/15 hover:border-white/40"}`}>
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="rate"
                            checked={selectedRate?.id === rt.id}
                            onChange={() => setSelectedRate(rt)}
                            data-testid={`rate-${rt.id}`}
                          />
                          <div>
                            <p className="font-semibold text-sm">{rt.name}</p>
                            {(rt.min_delivery_days || rt.max_delivery_days) && (
                              <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-0.5">
                                {rt.min_delivery_days}–{rt.max_delivery_days} business days
                              </p>
                            )}
                          </div>
                        </div>
                        <span className="font-bold text-white">{fmt(rt.rate, rt.currency)}</span>
                      </label>
                    ))}
                  </div>
                )}
                {rates.length === 0 && !quoteError && !quoteLoading && (
                  <p className="text-white/45 text-xs uppercase tracking-[0.2em]">Finish entering your address and we&rsquo;ll fetch a live rate.</p>
                )}
              </section>

              {/* Fulfillment expectations — visible right before the buyer commits */}
              <section data-testid="merch-fulfillment-note" className="border border-white/15 bg-[#0F0F0F] p-5 space-y-2">
                <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF1B91] flex items-center gap-2">
                  <Truck size={12} weight="bold" /> Merchandise processing &amp; delivery
                </p>
                <p className="text-white/80 text-xs leading-relaxed">
                  All merchandise is made to order rather than produced in bulk. Most orders arrive within approximately <strong className="text-white">2–3 weeks</strong>, while large orders or heavily customized items may take up to <strong className="text-white">4 weeks</strong>. Because products may be fulfilled from different facilities, items in the same order may arrive separately and have <strong className="text-white">separate tracking numbers</strong>.
                </p>
                <p className="text-white/55 text-[11px] italic leading-relaxed">
                  The merch shop stays open beyond September and after the challenge ends — you can keep purchasing Sweat With Slay Legacy merch year-round.
                </p>
              </section>

              {error && (
                <div className="border border-red-500 bg-red-500/10 p-4 text-sm text-red-300" data-testid="checkout-error">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {/* Right: order summary */}
        <OrderSummary
          isCartMode={isCartMode}
          cart={cart}
          product={product}
          variant={variant}
          activeMockup={activeMockup}
          quantity={quantity}
          itemTotal={itemTotal}
          shipping={shipping}
          grandTotal={grandTotal}
          selectedRate={selectedRate}
          submitting={submitting}
          shippingComplete={shippingComplete}
          recipient={recipient}
          onCheckout={checkout}
          provider={provider}
        />
      </div>
      {sizeGuideOpen && product && (
        <SizeGuideModal
          productId={product.id}
          productName={product.name}
          onClose={() => setSizeGuideOpen(false)}
        />
      )}
    </main>
  );
}
